import { getLocationHeader } from '@patrianna-ui-features/location-verification'
import { errorCodeGpsFeatureHandler } from '@patrianna-ui-features/location-verification/src/data-access/store/actions'
import { setGpsTriggerCookie } from '@patrianna-ui-features/location-verification/src/utils'
import { useRouter, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { CUSTOM_EVENT } from 'config/customEvent'
import useDomEvent from 'hooks/useDomEvent'
import { serviceDependencies } from 'src/services'
import { useAppDispatch } from 'src/store/hooks'
import { openSnackbar } from 'store/modules/snackbar/actions'

import type { TempToken } from '@patrianna/shared-patrianna-types/services/tokens'
import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import type { GetTemporaryTokenRequest } from '@patrianna/shared-patrianna-types/websocket/requests'
import type { GetTemporaryTokenResponse } from '@patrianna/shared-patrianna-types/websocket/response'
import type { SupportedCurrencies } from 'types/currencies'

export function useFetchExternalGameToken() {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const pathname = usePathname()

  const fetchGameToken = async (dataRequest: {
    code: string
    counterparty: string
    currency: SupportedCurrencies
    mode?: 'mini'
  }) => {
    const data: GetTemporaryTokenRequest = {
      type: 'GetTemporaryTokenRequest',
      ...dataRequest,
    }

    try {
      const customHeaders = getLocationHeader()
      const res = await serviceDependencies.gateway.emit<GetTemporaryTokenResponse>(data, undefined, customHeaders)

      return Promise.resolve(res as TempToken)
    } catch (err) {
      const errorCode = err?.status?.errorCode

      // handle error codes from "location verification" feature
      if (errorCodeGpsFeatureHandler[errorCode]) {
        setGpsTriggerCookie(`gameplay_${dataRequest.currency.toLowerCase()}`)
        dispatch(errorCodeGpsFeatureHandler[errorCode](err, router.replace, pathname, dataRequest.currency))
      } else {
        dispatch(openSnackbar({ message: err?.status?.errorText || err?.status?.errorCodes }))
        // rest of the codes
        dispatch(openSnackbar({ message: err?.status?.errorText || errorCode }))
        dispatch(serviceDependencies.errorHandler(err, data))

        return Promise.reject(err)
      }
    }
  }

  return { fetchGameToken }
}

export function useLoadExternalGame(gameProduct: GameProduct, currency: SupportedCurrencies, skip?: boolean) {
  const [slotToken, setSlotToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const { fetchGameToken } = useFetchExternalGameToken()

  async function fetchToken() {
    try {
      setLoading(true)
      const token = await fetchGameToken({
        code: gameProduct.code,
        counterparty: gameProduct.provider,
        currency,
      })
      setSlotToken(token)
    } catch (e) {
      setSlotToken(null)
    } finally {
      setLoading(false)
    }
  }

  useDomEvent(CUSTOM_EVENT.GAME_RELOAD, fetchToken, [gameProduct.code, gameProduct.provider, currency])

  useEffect(() => {
    if (gameProduct.code && !skip) {
      fetchToken()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameProduct.code, skip])

  return {
    slotToken,
    loading,
  }
}
